import { ApolloError } from 'apollo-boost';
import { FormikErrors } from 'formik';
import { CF_RAY_ID_REGEXP, NETWORK_ERROR_CODE, NETWORK_TOO_MANY_REQUESTS_CODE } from '../constants';
import copy from '../copy';
import { isServerParseError } from '@do/graphql-client';

interface APIError {
  code: string;
  field?: string;
}
interface ErrorMessage {
  rawData?: unknown;
  message: {
    title?: string | null;
    details: string;
  };
  field?: string | null;
}

function isAPIErrors(response: unknown): response is APIError[] {
  return Array.isArray(response) && response.every((item) => typeof item === 'object' && 'code' in item);
}

// cache the session data locally and check for localStorage on window
// as users can disable it, and we dont want this to prevent logging in

let sessionData: string | null;
const LS_KEY = 'login_session_data';
const VERIFIED_INLINE_ERROR_SHAPES = [/You have reached your Rate Limit/i];

// Adapted from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
function isStorageAvailable(): boolean {
  let storage;
  try {
    storage = window.localStorage;
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return !!(
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

export function addSessionData<T extends object>(values: T = {} as any): T & { session_data: string } {
  if (!sessionData) {
    if (isStorageAvailable()) {
      sessionData = window.localStorage.getItem(LS_KEY);
    } else {
      sessionData = null;
    }
  }
  return { ...values, session_data: sessionData ?? '' };
}

export const saveSessionData = (newSessionData?: string | null): void => {
  if (newSessionData) {
    sessionData = newSessionData;
    if (isStorageAvailable()) {
      window.localStorage.setItem(LS_KEY, sessionData);
    }
  }
};

export const getSessionData = (sessionItem: string): string => {
  return (isStorageAvailable() && window.localStorage.getItem(sessionItem)) || '';
};

/**
 * The graphql server returns an error with a shape
 *
 *   { code: 'email_exists', field: 'email' }
 *
 * We want to pluck out this error, and transform 'code' into a string
 */

const processError = (error: ApolloError): ErrorMessage => {
  const errorInResponse = error?.graphQLErrors?.[0];
  const networkError = error.networkError ?? undefined;

  if (
    isServerParseError(networkError) &&
    networkError.statusCode === NETWORK_ERROR_CODE &&
    (networkError.bodyText || '').includes('Sorry, you have been blocked')
  ) {
    const rayIdMatches = new RegExp(CF_RAY_ID_REGEXP, 'gi').exec(networkError.bodyText || '');
    let rayId = '';

    if (rayIdMatches) {
      rayId = rayIdMatches.length > 3 ? rayIdMatches[3] : rayIdMatches[2];
    }

    return {
      message: {
        title: copy.errors.request_blocked.title,
        details: `${copy.errors.request_blocked.details}${rayId ? ` and reference Ray ID: ${rayId.trim()}.` : '.'}`,
      },
      field: null,
    };
  } else if (!errorInResponse) {
    // if theres an verified inline message (rate limit etc) use that
    const message = error?.message;
    if (message && VERIFIED_INLINE_ERROR_SHAPES.find((messageRegex) => message.match(messageRegex))) {
      return {
        message: {
          title: copy.error_with_message_title,
          details: error?.message,
        },
      };
    }
    return {
      message: copy.errors.unknown,
      field: null,
    };
  }

  const errorCode = errorInResponse?.message;
  const errorMessage = copy.errors[errorCode as keyof typeof copy.errors] || copy.errors.unknown;

  // gmogan: As of 2024-08-14 I don't think this is actually used
  // but the typescript forcing was already here, and now its super isolated
  if (errorInResponse && 'field' in errorInResponse) {
    return {
      message: errorMessage,
      field: errorInResponse.field as string,
    };
  }
  return {
    message: errorMessage,
    field: null,
  };
};

const getRequestIdFromErrorResponse = (errorResponse: ApolloError): string | undefined => {
  return (
    (errorResponse.networkError as { extensions?: { requestId?: string } })?.extensions?.requestId ||
    errorResponse.graphQLErrors?.find((error) => (error.extensions as { requestId?: string })?.requestId)?.extensions
      ?.requestId
  );
};

export const handleFormError = (
  setErrors: (errs: FormikErrors<Record<string, unknown>>) => void,
  setGenericServerError: (err: any) => void,
): ((errorResponse: ApolloError) => void) => {
  return (errorResponse: ApolloError) => {
    if (
      errorResponse instanceof ReferenceError ||
      errorResponse instanceof RangeError ||
      errorResponse instanceof EvalError ||
      errorResponse instanceof SyntaxError ||
      errorResponse instanceof TypeError ||
      errorResponse instanceof URIError ||
      errorResponse instanceof AggregateError
    ) {
      throw errorResponse;
    }

    const { message, field } = processError(errorResponse);
    if (field) {
      return setErrors({ [field]: message.details });
    }

    const requestId = getRequestIdFromErrorResponse(errorResponse);
    setGenericServerError({ ...message, requestId });
  };
};

export const handlePlainFetchError = async (
  errorResponse: Response,
  setErrors: (errs: FormikErrors<Record<string, unknown>>) => void,
  setGenericServerError: (err: any) => void,
): Promise<APIError[] | undefined> => {
  const { message, field, rawData } = await processFetchError(errorResponse);
  if (field) {
    setErrors({ [field]: message.details });
    const errors = (rawData as any)?.errors;
    return isAPIErrors(errors) ? errors : undefined;
  }
  setGenericServerError(message);
};

const processFetchError = async (fetchResponse: Response): Promise<ErrorMessage> => {
  const data = await fetchResponse.json();

  if (fetchResponse.status === NETWORK_TOO_MANY_REQUESTS_CODE && data?.message) {
    return {
      message: {
        title: copy.error_with_message_title,
        details: data?.message,
      },
    };
  }

  const err = data?.errors?.[0];
  const errorCode = err?.code;
  const errorMessage = copy.errors[errorCode as keyof typeof copy.errors] || copy.errors.unknown;

  return {
    rawData: data,
    field: err?.field,
    message: errorMessage,
  };
};
